import React from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { LeadCard } from './LeadCardVisuals';

/**
 * PipelineColumn — vertical drop zone for one stage. Owns nothing;
 * receives drag callbacks + selection state from LeadsPipelinePage.
 */
export const PipelineColumn = ({
  stage,
  leads,
  onLeadClick,
  onDrop,
  isDraggingOver,
  selectedLeads,
  onToggleSelection,
}) => {
  const stageLeads = leads.filter((l) => l.stage === stage.id);
  const totalValue = stageLeads.reduce(
    (sum, l) => sum + (l.ai_prediction?.expected_revenue || 0),
    0,
  );

  return (
    <div
      className={cn(
        'flex-shrink-0 w-80 bg-slate-50 rounded-xl p-3 transition-all',
        isDraggingOver && 'bg-blue-50 ring-2 ring-blue-300',
      )}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => onDrop(e, stage.id)}
      data-testid={`pipeline-column-${stage.id}`}
    >
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <span className="text-lg">{stage.emoji}</span>
          <h3 className="font-semibold text-slate-700">{stage.label}</h3>
          <Badge variant="secondary" className="text-xs">
            {stageLeads.length}
          </Badge>
        </div>
        {totalValue > 0 && (
          <span className="text-xs text-slate-500 font-medium">
            ${totalValue.toLocaleString()}
          </span>
        )}
      </div>

      <div className="space-y-3 min-h-[200px] max-h-[calc(100vh-300px)] overflow-y-auto">
        {stageLeads.map((lead) => (
          <div
            key={lead.lead_id}
            draggable
            onDragStart={(e) => e.dataTransfer.setData('leadId', lead.lead_id)}
            className="cursor-grab active:cursor-grabbing"
          >
            <LeadCard
              lead={lead}
              onClick={onLeadClick}
              isSelected={selectedLeads?.has(lead.lead_id)}
              onSelect={onToggleSelection}
            />
          </div>
        ))}
        {stageLeads.length === 0 && (
          <div className="text-center py-8 text-slate-400">
            <p className="text-sm">No leads in this stage</p>
          </div>
        )}
      </div>
    </div>
  );
};
